/**
 * Token Refresh Utilities
 *
 * NOTE: Firebase automatically refreshes tokens when you call getIdToken().
 * This utility is kept as a backup mechanism for proactive refresh,
 * but the primary token refresh happens on-demand in the API interceptor.
 *
 * With Firebase persistence enabled, sessions can last as long as the
 * refresh token is valid (typically 30+ days for local persistence).
 */

import { refreshToken } from '@/services/auth'
import { useAuthStore } from '@/stores/auth'

let refreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Start proactive token refresh (backup mechanism)
 * Refreshes token periodically to keep it fresh
 *
 * This is optional since API calls will refresh on-demand,
 * but helps maintain a fresh token for long-idle sessions
 */
export function startTokenRefresh() {
  // Clear any existing timer
  stopTokenRefresh()

  // Refresh every 30 minutes as a backup (primary refresh is on-demand via API calls)
  const REFRESH_INTERVAL = 30 * 60 * 1000 // 30 minutes

  const scheduleRefresh = async () => {
    try {
      const newToken = await refreshToken()

      if (newToken) {
        const authStore = useAuthStore()
        authStore.setToken(newToken)
        // Schedule next refresh
        refreshTimer = setTimeout(scheduleRefresh, REFRESH_INTERVAL)
      } else {
        // Token refresh failed - user is logged out
        console.warn('Proactive token refresh failed - user may be logged out')
        stopTokenRefresh()
      }
    } catch (error) {
      console.error('Proactive token refresh error:', error)
      stopTokenRefresh()
    }
  }

  // Start the refresh cycle
  refreshTimer = setTimeout(scheduleRefresh, REFRESH_INTERVAL)
}

/**
 * Stop automatic token refresh
 */
export function stopTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
    console.log('Token refresh stopped')
  }
}

/**
 * Manually refresh token immediately
 */
export async function forceTokenRefresh(): Promise<string | null> {
  try {
    const newToken = await refreshToken()
    if (newToken) {
      const authStore = useAuthStore()
      authStore.setToken(newToken)
    }
    return newToken
  } catch (error) {
    console.error('Failed to force refresh token:', error)
    return null
  }
}
